import { Alert, Button, Form, Modal } from "antd";
import { Rule } from "antd/lib/form";
import { AxiosError } from "axios";
import CustomerSelector from "components/CustomerSelector";
import SilentUserSelector from "components/SilentUserSelector";
import { Roles } from "context/auth";
import { ApplicationUser, SilentUser } from "models/applicationUsers";
import { SetStateAction, useState } from "react";
import { SilentAccountMergeResult, useSilentAccountMerge } from "./useSilentAccountMerge";

interface MergeAccountForm {
    breaker?: ApplicationUser;
    trueUser?: SilentUser;
    falseUser?: SilentUser;
}

const MergeAccountDisplay = ({
    cardsEffected,
    packagesEffected,
    transactionsEffected
}: SilentAccountMergeResult) => {
    return <ul style={{ listStyleType: 'none' }}>
        <li>{cardsEffected} Card(s)</li>
        <li>{packagesEffected} Package(s)</li>
        <li>{transactionsEffected} Transaction(s)</li>
    </ul>;
}

const MergeSilentAccountsModal = () => {
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm<MergeAccountForm>();
    const { breaker, trueUser, falseUser } = Form.useWatch([], form) ?? {};
    const { mergeAccounts, isLoading } = useSilentAccountMerge();
    const [confirmResult, setConfirmResult] = useState<SilentAccountMergeResult | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [successResult, setSuccessResult] = useState<SilentAccountMergeResult | undefined>();

    const submit = async (dryRun: boolean, setResult: (value: SetStateAction<SilentAccountMergeResult | undefined>) => void) => {
        await form.validateFields();

        try {
            setError(undefined);

            const result = await mergeAccounts({
                breaker: breaker!,
                trueUser: trueUser!,
                falseUser: falseUser!,
                dryRun
            });

            setResult(result);
        } catch (err) {
            if (err instanceof AxiosError) {
                const reason = err.response!.data!.detail!;
                setError(reason);
            }
        }
    }

    const performDryRun = async () => {
        await submit(true, setConfirmResult);
    }

    const performMerge = async () => {
        await submit(false, setSuccessResult);
    }

    const close = () => {
        form.resetFields();
        setOpen(false);
        setConfirmResult(undefined);
        setSuccessResult(undefined);
        setError(undefined);
    }

    const userValidator = async (_: Rule) => {
        const { trueUser, falseUser } = form.getFieldsValue();

        if (!trueUser || !falseUser) {
            // missing one or both users, it'll get caught by the required field validator
            return;
        }

        if (trueUser.id === falseUser.id) {
            throw new Error('The correct and erroneous user cannot be the same!');
        }
    }

    return <>
        <Button onClick={() => setOpen(true)} type="primary">Merge Silent Accounts</Button>
        <Modal
            title="Merge Silent Accounts"
            closable={false}
            keyboard={false}
            maskClosable={false}
            destroyOnClose={true}
            open={open}
            onOk={async () => {
                if (!confirmResult) {
                    await performDryRun();
                } else {
                    await performMerge();
                }
            }}
            onCancel={close}
            okText={!confirmResult ? 'OK' : 'Yes, I am sure!'}
            okButtonProps={successResult ? { style: { display: 'none' } } : undefined}
            cancelText={successResult ? 'Close' : 'Cancel'}
        >
            <Form form={form} layout="vertical" disabled={isLoading || !!confirmResult}>
                <Form.Item
                    name="breaker"
                    label="Select Breaker"
                    rules={[{ required: true, message: "Breaker is required!" }]}
                >
                    <CustomerSelector
                        roles={[Roles.Breaker]}
                        onSelected={(breaker) => form.setFieldsValue({ breaker })}
                        onClear={() => form.setFieldsValue({ breaker: undefined })}
                    />
                </Form.Item>

                <Form.Item
                    name="trueUser"
                    label="Select the correct user account"
                    rules={[{ required: true, message: "Correct user is required!" }]}
                >
                    <SilentUserSelector
                        ownerId={breaker?.id ?? ''}
                        disabled={!breaker}
                        placeholder={breaker === undefined ? "Select a breaker first" : undefined}
                        onSelected={(trueUser) => form.setFieldsValue({ trueUser })}
                        onClear={() => form.setFieldsValue({ trueUser: undefined })}
                    />
                </Form.Item>

                <Form.Item
                    name="falseUser"
                    label="Select the erroneous user account"
                    rules={[
                        { required: true, message: "Erroneous user is required!" },
                        { validator: userValidator }
                    ]}
                >
                    <SilentUserSelector
                        ownerId={breaker?.id ?? ''}
                        disabled={!breaker}
                        placeholder={breaker === undefined ? "Select a breaker first" : undefined}
                        onSelected={(falseUser) => form.setFieldsValue({ falseUser })}
                        onClear={() => form.setFieldsValue({ falseUser: undefined })}
                    />
                </Form.Item>

                {confirmResult && !successResult && <Alert showIcon
                    type="warning"
                    message={`${trueUser!.userName} will take ownership of:`}
                    description={<>
                        <MergeAccountDisplay {...confirmResult} />
                        and {falseUser!.userName} will be DELETED. Are you sure?
                    </>}
                />}

                {error && <Alert showIcon
                    type="error"
                    message="Unable to Merge Accounts"
                    description={error}
                />}

                {successResult && <Alert showIcon
                    type="success"
                    message={`${trueUser!.userName} has taken ownership of:`}
                    description={<>
                        <MergeAccountDisplay {...successResult} />
                        and {falseUser!.userName} has been deleted.
                    </>}
                />}
            </Form>
        </Modal>
    </>;
}

export default MergeSilentAccountsModal;